// src/utils/mySeason.ts
//
// „Moje sezóna" (nice-to-have po redesignu, rozhodnuto 2. 10. 2026): osobní
// přehled přihlášeného střelce za rok a disciplínu na stránce Výsledky –
// z výsledků, které stránka už načítá pro roční hodnocení (hodnocené závody
// roku). Žádná vlastní data ani čtení navíc; stejné definice jako tabulky:
// pořadí = místo v seřazených výsledcích (rankRaceEntries – u závodů s pravidly
// shody 4b sdílené místo), % = podíl na
// vítězi závodu (computeRacePercent), roční pořadí = pozice v ročním
// hodnocení (computeSeasonRanking) se stejným nastavením jako jeho tabulka.

import { stripDiacritics } from "../sepa";
import type { DisciplineId } from "./disciplines";
import { resolveEntryCategoryId, type ResultCategoryId } from "./categories";
import {
  computeRacePercent,
  computeSeasonRanking,
  isSameShooter,
  normalizeSequenceNumber,
  raceTopScore,
  rankCategoryEntries,
  rankRaceEntries,
  seasonRaces,
  type BestRacesCount,
  type RaceResult,
  type SeasonRace,
  type SeasonValueMode,
} from "./seasonRanking";

export interface MySeasonRaceResult {
  raceId: string;
  raceName: string;
  /** "YYYY-MM-DD". */
  date: string;
  /** Místo v celkových výsledcích závodu (1 = vítěz) a počet střelců. */
  rank: number;
  of: number;
  /** Kategorie střelce v závodě (resolveEntryCategoryId); null = nezařazen. */
  categoryId: ResultCategoryId | null;
  /** Místo mezi střelci téže kategorie; null u nezařazeného. */
  categoryRank: number | null;
  categoryOf: number | null;
  points: number;
  /** % z vítěze závodu (2 desetinná místa). */
  percent: number;
}

export interface MySeasonPosition {
  rank: number;
  of: number;
}

export interface MySeason {
  year: number;
  discipline: DisciplineId;
  /** Hodnocené závody roku a disciplíny. */
  ratedRaces: number;
  /** Z nich s načtenými výsledky (zbytek se ještě načítá nebo selhal). */
  loadedRaces: number;
  /** Závody, ve kterých střelec má výsledek, vzestupně podle data. */
  results: MySeasonRaceResult[];
  /** Nejlepší umístění (nejnižší místo, při shodě vyšší %). */
  best: MySeasonRaceResult;
  /** Průměr % z odstřílených závodů (2 desetinná místa). */
  averagePercent: number;
  /** Pozice v ročním hodnocení (všichni) se zadaným nastavením. */
  seasonPosition: MySeasonPosition | null;
  /** Pozice v ročním hodnocení kategorie střelce. */
  seasonCategoryPosition: (MySeasonPosition & { categoryId: ResultCategoryId }) | null;
}

/** Přihlášený střelec – podle čeho se pozná jeho záznam ve výsledcích. */
export interface Me {
  uid?: string | null;
  /** Členské číslo (users.sequenceNumber). */
  sequenceNumber: string | number | null | undefined;
  firstName?: string | null;
  lastName?: string | null;
}

/** Slova jména bez diakritiky a interpunkce ("Novák Jan ml." → novak, jan, ml). */
const nameTokens = (value: unknown): string[] =>
  typeof value === "string"
    ? stripDiacritics(value)
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean)
    : [];

const shareToken = (a: string[], b: Set<string>): boolean => a.some((t) => b.has(t));

/**
 * Je záznam výsledků můj? Shoda userId (publikace ze živého bodování) stačí.
 * Jinak (import, starší publikace, profil přenesený na nové uid při
 * registraci – záznam nese uid starého importovaného profilu) podle klíče
 * a navíc jména: klíč hosta je jeho startovní číslo a může se krýt s cizím
 * členským číslem – příjmení musí sedět, křestní jméno taky, má-li záznam
 * víc slov (pořadí slov nehraje roli). Bez jména v profilu stačí klíč.
 */
export function isMyEntry(
  entry: { sequenceNumber: string; rawName?: string; userId?: unknown },
  me: Me
): boolean {
  if (typeof entry.userId === "string" && entry.userId !== "" && !!me.uid && entry.userId === me.uid) {
    return true;
  }
  if (!isSameShooter(me.sequenceNumber, entry.sequenceNumber)) return false;
  const last = nameTokens(me.lastName);
  const first = nameTokens(me.firstName);
  if (last.length === 0 && first.length === 0) return true;
  const raw = nameTokens(entry.rawName);
  if (raw.length === 0) return true;
  const rawSet = new Set(raw);
  if (last.length > 0 && !shareToken(last, rawSet)) return false;
  if (first.length > 0 && raw.length > 1 && !shareToken(first, rawSet)) return false;
  return true;
}

export interface MySeasonInput {
  /** Všechny závody (useRaces.allRaces) – vybírají se hodnocené závody roku a disciplíny. */
  races: SeasonRace[];
  /** Načtené výsledky podle id závodu (seřazené – loadRaceResults / sortRaceEntries). */
  entriesByRace: { [raceId: string]: RaceResult[] | undefined };
  year: number;
  discipline: DisciplineId;
  /** Přihlášený střelec (uid, členské číslo, jméno – viz isMyEntry). */
  me: Me;
  /** Nastavení ročního hodnocení té disciplíny (jako jeho tabulka). */
  mode: SeasonValueMode;
  bestN: BestRacesCount;
  /** Dnešní datum "YYYY-MM-DD" (testy); výchozí = dnes. */
  today?: string;
}

/** Dnešní datum "YYYY-MM-DD" v místním čase. */
const todayIso = (): string => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Závody sezóny, které se už konaly (nebo mají výsledky) – vypsané budoucí
 * hodnocené závody se do „X z Y" ani do načítání nepočítají.
 */
export function heldSeasonRaces(
  races: SeasonRace[],
  year: number,
  discipline: DisciplineId,
  entriesByRace: { [raceId: string]: RaceResult[] | undefined },
  today: string = todayIso()
): SeasonRace[] {
  return seasonRaces(races, year, discipline).filter((race) => {
    const loaded = entriesByRace[race.id];
    return race.date <= today || (!!loaded && loaded.length > 0);
  });
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** Osobní přehled sezóny; null = střelec v sezóně nemá žádný načtený výsledek. */
export function computeMySeason(input: MySeasonInput): MySeason | null {
  const { entriesByRace, year, discipline, me } = input;
  const mySeq = me.sequenceNumber;
  if (mySeq === null || mySeq === undefined || String(mySeq).trim() === "") return null;

  const rated = heldSeasonRaces(input.races, year, discipline, entriesByRace, input.today);
  let loadedRaces = 0;
  const results: MySeasonRaceResult[] = [];

  rated.forEach((race) => {
    const raceResults = entriesByRace[race.id];
    if (!raceResults) return;
    loadedRaces += 1;
    const index = raceResults.findIndex((r) => isMyEntry(r.entry, me));
    if (index < 0) return;
    const mine = raceResults[index];
    const categoryId = resolveEntryCategoryId(mine.entry);
    // Místo jako tabulka závodu (fáze 4b: u závodu s pravidly shody LKS
    // sdílené místo; starší závod pořadí jako dřív)
    const rank = rankRaceEntries(raceResults, race).places.get(mine) as number;
    let categoryRank: number | null = null;
    let categoryOf: number | null = null;
    if (categoryId) {
      // Pořadí v kategorii jako filtr kategorie ve výsledcích (fáze 4a, C2)
      const sameCategory = rankCategoryEntries(raceResults, (r) => resolveEntryCategoryId(r.entry) === categoryId, race);
      categoryOf = sameCategory.ordered.length;
      categoryRank = sameCategory.places.get(mine) as number;
    }
    results.push({
      raceId: race.id,
      raceName: race.name,
      date: race.date,
      rank,
      of: raceResults.length,
      categoryId,
      categoryRank,
      categoryOf,
      points: mine.entry.matchPoints,
      percent: computeRacePercent(mine.entry, { topScore: raceTopScore(raceResults) }),
    });
  });

  if (results.length === 0) return null;

  const best = results.reduce((a, b) =>
    b.rank < a.rank || (b.rank === a.rank && b.percent > a.percent) ? b : a
  );
  const averagePercent = round2(results.reduce((sum, r) => sum + r.percent, 0) / results.length);

  // Roční hodnocení slučuje řádky podle klíče: pro „moje" pořadí patří pod
  // můj klíč jen moje záznamy (host se startovním číslem = mé číslo jinam)
  // a moje záznamy pod jiným klíčem (shoda uid) pod můj klíč
  const myKey = normalizeSequenceNumber(mySeq);
  const myEntriesByRace: { [raceId: string]: RaceResult[] | undefined } = {};
  Object.keys(entriesByRace).forEach((raceId) => {
    const list = entriesByRace[raceId];
    if (!list) return;
    myEntriesByRace[raceId] = list.map((r) => {
      const keyIsMine = isSameShooter(mySeq, r.entry.sequenceNumber);
      const mine = isMyEntry(r.entry, me);
      if (keyIsMine && !mine) {
        return { ...r, entry: { ...r.entry, sequenceNumber: `${r.entry.sequenceNumber}~${raceId}` } };
      }
      if (!keyIsMine && mine) {
        return { ...r, entry: { ...r.entry, sequenceNumber: myKey } };
      }
      return r;
    });
  });

  const rankingInput = {
    races: input.races,
    entriesByRace: myEntriesByRace,
    year,
    discipline,
    mode: input.mode,
    bestN: input.bestN,
  };
  const overall = computeSeasonRanking({ ...rankingInput, category: "all" });
  const overallIndex = overall.rows.findIndex((row) => isSameShooter(mySeq, row.sequenceNumber));
  const seasonPosition =
    overallIndex >= 0 ? { rank: overallIndex + 1, of: overall.rows.length } : null;

  let seasonCategoryPosition: MySeason["seasonCategoryPosition"] = null;
  const myRow = overallIndex >= 0 ? overall.rows[overallIndex] : null;
  if (myRow && myRow.categoryId) {
    const inCategory = computeSeasonRanking({ ...rankingInput, category: myRow.categoryId });
    const categoryIndex = inCategory.rows.findIndex((row) =>
      isSameShooter(mySeq, row.sequenceNumber)
    );
    if (categoryIndex >= 0) {
      seasonCategoryPosition = {
        rank: categoryIndex + 1,
        of: inCategory.rows.length,
        categoryId: myRow.categoryId,
      };
    }
  }

  return {
    year,
    discipline,
    ratedRaces: rated.length,
    loadedRaces,
    results,
    best,
    averagePercent,
    seasonPosition,
    seasonCategoryPosition,
  };
}
